import { Injectable, inject } from '@angular/core';
import { CanMatch, Router, Route, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { SymphonyIdService } from '../services/symphony-id/symphony-id.service';

@Injectable({
  providedIn: 'root',
})
export class SymphonyIdGuard implements CanMatch {
  private symphonyIdService = inject(SymphonyIdService);
  private router = inject(Router);
  private processingUids = new Set<string>();

  canMatch(
    route: Route,
    segments: UrlSegment[],
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const currentUrl = new URL(window.location.href);
    const uidEntry = Array.from(currentUrl.searchParams.entries()).find(
      ([key]: [string, string]) => key.toLowerCase() === 'uid',
    );
    const uid = uidEntry?.[1];

    if (!uid) {
      return true;
    }

    // Use segments to build the proper key, not router.url (which may be /)
    const targetPath = '/' + segments.map((s) => s.path).join('/');
    const uidKey = `${uid}:${targetPath}`;

    if (this.processingUids.has(uidKey)) {
      // Already processing this UID for this path, allow navigation
      return true;
    }

    this.processingUids.add(uidKey);
    return this.symphonyIdService.handleUid(uid).pipe(
      map(() => {
        this.processingUids.delete(uidKey);
        // Keep UID in URL - don't clear from redirect state
        return true;
      }),
      catchError(() => {
        this.processingUids.delete(uidKey);
        // Keep UID in URL even on error
        return of(true);
      }),
    );
  }
}
